import { chain, splice, push, set, setIn, unset } from 'icepick';
import zipObject from 'lodash/zipObject';
import { ChangeEventTypes } from '@atlassian/jira-issue-view-model/src/change-type';
import { UNASSIGNED_EPIC_ID } from '../../../../common/constants';
import { getCardListId } from '../../../../common/utils';
import { BACKLOG } from '../../../../model/constants';
import { SET_APP_DATA, type SetAppDataAction } from '../../../actions/app';
import {
	CARD_CREATE_MULTIPLE_REQUEST,
	CARD_CREATE_SUCCESS,
	CARD_CREATE_END_OF_BACKLOG_OPTIMISTIC,
	CARD_CREATE_UNMAPPED_WITH_ISSUE_LINK,
	type CardCreateSuccessAction,
	type CardCreateEndOfBacklogOptimisticAction,
	type CardCreateMultipleRequestAction,
	type CardCreateUnmappedWithIssueLinkAction,
} from '../../../actions/cards/create';
import {
	CARD_DELETE,
	CARD_DELETE_REQUEST,
	type CardDeleteRequestAction,
	type CardDeleteAction,
} from '../../../actions/cards/delete';
import {
	CARDS_MOVE_REQUEST,
	CARDS_MOVE_SET,
	type CardsMoveRequestAction,
	type CardsMoveSetAction,
} from '../../../actions/cards/move';
import {
	type SprintCreateRequestAction,
	type SprintCreateSuccessAction,
	SPRINT_CREATE_REQUEST,
	SPRINT_CREATE_SUCCESS,
} from '../../../actions/sprints/create';
import {
	SPRINT_DELETE_REQUEST,
	type SprintDeleteRequestAction,
} from '../../../actions/sprints/delete';
import type { CardOrderEntityState } from './types';

type Action =
	| SetAppDataAction
	| CardsMoveRequestAction
	| CardsMoveSetAction
	| CardDeleteAction
	| CardCreateSuccessAction
	| CardDeleteRequestAction
	| SprintCreateRequestAction
	| SprintCreateSuccessAction
	| SprintDeleteRequestAction
	| CardCreateEndOfBacklogOptimisticAction
	| CardCreateUnmappedWithIssueLinkAction
	| CardCreateMultipleRequestAction;

const defaultState: CardOrderEntityState = {};

const getNewCardOrder = (action: Action) => {
	if (action.type === CARDS_MOVE_REQUEST) {
		return action.payload.newCardOrder;
	}
	return undefined;
};

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (
	state: CardOrderEntityState = defaultState,
	action: Action,
): CardOrderEntityState => {
	switch (action.type) {
		case SET_APP_DATA:
			return action.payload.entities.cardOrder;

		case CARDS_MOVE_SET:
		case CARDS_MOVE_REQUEST: {
			const newState = chain(state);
			const {
				payload: { cardIds, beforeCardId, afterCardId, cardListId },
			} = action;
			const derivedCardListId = getCardListId(cardListId);

			if (cardListId === String(UNASSIGNED_EPIC_ID)) {
				return state;
			}

			// Remove the moved cardIds from all cardlists, to be
			// returned to their new location below
			Object.keys(state).forEach((key) =>
				newState.setIn(
					[key],
					state[key].filter((cardId) => !cardIds.includes(cardId)),
				),
			);

			if (getNewCardOrder(action)) {
				return newState.setIn([derivedCardListId], getNewCardOrder(action)).value();
			}

			const cardIdList = newState.value()[derivedCardListId];
			const insertIndex = beforeCardId
				? cardIdList.indexOf(beforeCardId)
				: cardIdList.indexOf(afterCardId || 0) + 1;

			newState.setIn([derivedCardListId], splice(cardIdList, insertIndex, 0, ...cardIds));

			return newState.value();
		}
		case CARD_CREATE_MULTIPLE_REQUEST: {
			const { tempIds, listId, insertBefore } = action.payload;
			if (!insertBefore) {
				let updatedState = state[listId];
				tempIds.forEach((tempId) => {
					updatedState = push(updatedState, tempId);
				});
				return set(state, listId, updatedState);
			}
			const insertIndex: number = state[listId].indexOf(insertBefore);
			return set(state, listId, splice(state[listId], insertIndex, 0, ...tempIds));
		}
		case CARD_CREATE_SUCCESS: {
			const { card, listId, temporaryCardId } = action.payload;
			const index: number = state[listId].indexOf(temporaryCardId);
			return setIn(state, [listId, index], card.id);
		}

		case CARD_DELETE:
		case CARD_DELETE_REQUEST: {
			const cardListIds = Object.keys(state);
			const { cardId: deletedCardId } = action.payload;
			const filteredLists = cardListIds.map((cardListId) =>
				state[cardListId].filter((cardId) => cardId !== deletedCardId),
			);
			return zipObject(cardListIds, filteredLists);
		}
		case CARD_CREATE_UNMAPPED_WITH_ISSUE_LINK: {
			const cardListIds = Object.keys(state);
			const deletedCardIds = action.payload.cardsIdentifiers.map((cardIds) => cardIds.id);
			const filteredLists = cardListIds.map((cardListId) =>
				state[cardListId].filter((cardId) => !deletedCardIds.includes(cardId)),
			);
			return zipObject(cardListIds, filteredLists);
		}

		case SPRINT_CREATE_REQUEST: {
			return set(state, action.payload.temporaryCardListId, []);
		}

		case SPRINT_CREATE_SUCCESS: {
			return chain(state)
				.unset(action.payload.temporaryCardListId)
				.set(action.payload.sprint.id, [])
				.value();
		}

		case SPRINT_DELETE_REQUEST: {
			const {
				payload: { nextCardListId, sprintId },
			} = action;
			if (!nextCardListId) {
				return unset(state, sprintId);
			}
			return chain(state)
				.set(nextCardListId, state[sprintId].concat(state[nextCardListId]))
				.unset(sprintId)
				.value();
		}

		case CARD_CREATE_END_OF_BACKLOG_OPTIMISTIC: {
			const { changeEvent } = action.payload;
			if (changeEvent.type === ChangeEventTypes.CHILD_ISSUE_ADDED) {
				const {
					meta: {
						childIssue: { id },
					},
				} = changeEvent;
				return { ...state, [BACKLOG]: [...state[BACKLOG], Number(id)] };
			}
			return state;
		}
		default: {
			const _exhaustiveCheck: never = action;
			return state;
		}
	}
};
